import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface AdvertiseFormProps {
  selectedPlan?: string;
}

export default function AdvertiseForm({ selectedPlan }: AdvertiseFormProps) {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    plan: '',
    budget: '',
    duration: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, plan: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('plan', formData.plan);
      formBody.append('budget', formData.budget);
      formBody.append('duration', formData.duration);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d69e4f7mvg9ih2c7a03g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          name: '',
          email: '',
          phone: '',
          plan: '',
          budget: '',
          duration: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="inquiry-form" className="py-12 px-6 bg-gray-900">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-electric-blue font-medium text-xs uppercase tracking-wider">
              Contact Us
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-4">
              ビルボード広告掲載の
              <br />
              ご相談はこちら
            </h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              専任担当者が24時間以内にご連絡いたします。
              <br />
              まずはお気軽にご相談ください。
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-electric-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg text-electric-blue"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-0.5">24時間以内に返信</h4>
                  <p className="text-gray-400 text-xs">お問い合わせ後、担当者が迅速にご連絡いたします</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-electric-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-presentation-line text-lg text-electric-blue"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-0.5">無料コンサルティング</h4>
                  <p className="text-gray-400 text-xs">最適なプランをご提案。相談は完全無料です</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-electric-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-lg text-electric-blue"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-0.5">日本語対応</h4>
                  <p className="text-gray-400 text-xs">日本語スタッフが丁寧にサポートいたします</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              id="advertise-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-6 shadow-xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs"
                    placeholder="山田 太郎"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                  ご希望のプラン <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label
                    className={`relative flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.plan === 'スポットプラン'
                        ? 'border-electric-blue bg-electric-blue/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value="スポットプラン"
                      checked={formData.plan === 'スポットプラン'}
                      onChange={handleChange}
                      required
                      className="sr-only"
                    />
                    <div className={`w-8 h-8 rounded-md bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center mb-1.5`}>
                      <i className="ri-flashlight-line text-white text-sm"></i>
                    </div>
                    <span className="font-semibold text-gray-900 text-xs">スポットプラン</span>
                    <span className="text-gray-500 text-[10px] mt-0.5">1週間〜 / $5,000〜</span>
                    {formData.plan === 'スポットプラン' && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-electric-blue rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-[10px]"></i>
                      </div>
                    )}
                  </label>
                  <label
                    className={`relative flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.plan === 'スタンダードプラン'
                        ? 'border-electric-blue bg-electric-blue/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value="スタンダードプラン"
                      checked={formData.plan === 'スタンダードプラン'}
                      onChange={handleChange}
                      required
                      className="sr-only"
                    />
                    <div className={`w-8 h-8 rounded-md bg-gradient-to-br from-electric-blue to-cyan-400 flex items-center justify-center mb-1.5`}>
                      <i className="ri-star-line text-white text-sm"></i>
                    </div>
                    <span className="font-semibold text-gray-900 text-xs">スタンダードプラン</span>
                    <span className="text-gray-500 text-[10px] mt-0.5">1ヶ月〜 / $15,000〜</span>
                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-electric-blue text-white text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap">人気</span>
                    {formData.plan === 'スタンダードプラン' && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-electric-blue rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-[10px]"></i>
                      </div>
                    )}
                  </label>
                  <label
                    className={`relative flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.plan === 'プレミアムプラン'
                        ? 'border-electric-blue bg-electric-blue/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value="プレミアムプラン"
                      checked={formData.plan === 'プレミアムプラン'}
                      onChange={handleChange}
                      required
                      className="sr-only"
                    />
                    <div className={`w-8 h-8 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-1.5`}>
                      <i className="ri-vip-crown-line text-white text-sm"></i>
                    </div>
                    <span className="font-semibold text-gray-900 text-xs">プレミアムプラン</span>
                    <span className="text-gray-500 text-[10px] mt-0.5">3ヶ月〜 / $35,000〜</span>
                    {formData.plan === 'プレミアムプラン' && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-electric-blue rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-[10px]"></i>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    ご予算 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="〜$10,000">〜$10,000</option>
                    <option value="$10,000〜$50,000">$10,000〜$50,000</option>
                    <option value="$50,000〜$100,000">$50,000〜$100,000</option>
                    <option value="$100,000以上">$100,000以上</option>
                    <option value="未定・相談したい">未定・相談したい</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                    掲載期間 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="1週間">1週間</option>
                    <option value="2週間">2週間</option>
                    <option value="1ヶ月">1ヶ月</option>
                    <option value="3ヶ月">3ヶ月</option>
                    <option value="6ヶ月以上">6ヶ月以上</option>
                    <option value="未定・相談したい">未定・相談したい</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1.5 text-xs">
                  ご相談内容
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20 outline-none transition-all text-xs resize-none"
                  placeholder="広告の目的、ターゲット、ご希望のロケーションなどをお聞かせください"
                ></textarea>
                <p className="text-gray-400 text-[10px] mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-electric-blue to-cyan-400 text-white rounded-lg font-semibold text-sm hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    無料相談を申し込む
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <i className="ri-check-double-line text-green-600 text-lg"></i>
                  <p className="text-green-700 text-xs">
                    お問い合わせありがとうございます。24時間以内にご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600 text-lg"></i>
                  <p className="text-red-700 text-xs">
                    送信に失敗しました。お手数ですが、もう一度お試しください。
                  </p>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
